import { Injectable } from '@angular/core';
import { BehaviorSubject, of, EMPTY } from 'rxjs';
import { take, catchError } from 'rxjs/operators';
import { BureauPaymentsGridUpdateService } from '@app/bureau/payments/payment-status/entity-grid/services/bureau-payments-grid-update.service';
import { OnscreenMessagingService } from '@pattern-library/onscreen-messaging/onscreen-messaging.service';
import { BureauPaymentListItem } from '@app/bureau/payments/payment-status/models/bureau-payment-list-item';
import { HttpClient } from '@angular/common/http';
import { environment } from '@env/environment';
import { ActiveSecurityContextStateService } from '@security/active-security/active-security-context.state-service';
import { isDue } from '@utils/date-utils';
import { PaymentReversalConfirmationComponent } from '../../payment-reversal/payment-reversal-confirmation.component';

@Injectable({
  providedIn: 'root'
})
export class BureauPaymentStatusChangeService {
  protected apiRoot = `${environment.apiPaths.bureau}/${this.activeSecurity.activeBureau.id}/bank-files`;

  inFlight$ = new BehaviorSubject<boolean>(false);

  constructor(
    private gridUpdateService: BureauPaymentsGridUpdateService,
    private messaging: OnscreenMessagingService,
    private httpClient: HttpClient,
    private activeSecurity: ActiveSecurityContextStateService
  ) {}

  reversePaidByEmployerBankFileWithDateCheck(listItem: BureauPaymentListItem) {
    if (!isDue(listItem.submissionDate)) {
      this.reversePaidByEmployerBankFile(listItem.id);
    } else {
      this.messaging.openConfirmationComponentDialog(
        PaymentReversalConfirmationComponent,
        {},
        () => {
          this.reversePaidByEmployerBankFile(listItem.id);
        },
        'Confirm',
        'Cancel'
      );
    }
  }

  reversePaidByEmployerBankFile(bankFileId: number) {
    this.inFlight$.next(true);

    this.reversePaidByEmployerBankFileApiCall$(bankFileId)
      .pipe(
        take(1),
        catchError((error) => {
          this.handleApiError(error);
          return EMPTY;
        })
      )
      .subscribe({
        next: () => {
          this.messaging.success('Payment has been reverted to “New/Not authorised” status.');
          this.gridUpdateService.trigger();
        },
        complete: () => {
          this.inFlight$.next(false);
        }
      });
  }

  private handleApiError(error: any): void {
    // All error statuses share the { error } response shape.
    const errorMessage = [403, 404, 409, 422, 500].includes(error.status) ? (error.error?.error ?? '') : '';

    this.messaging.openAlertDialog({
      title: 'An error occurred while marking the payment as Paid by bureau.',
      message: errorMessage,
      showIcon: true,
      displayTitleAsHeader: true
    });
  }

  private reversePaidByEmployerBankFileApiCall$(bankFileId: number) {
    const url = `${this.apiRoot}/${bankFileId}/reverse-paid-by-employer`;
    return this.httpClient.post<boolean>(url, {});
  }
}
